import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { X } from "lucide-react";
import { useMountEffect } from "@/hooks/useMountEffect";
import { useTranslation } from "@/i18n/translations";
import { CollapsibleIntegration } from "@/components/CollapsibleIntegration";

/**
 * Short booking link block — styled identically to other integration cards
 * (IDENT, VK, Meta). Uses CollapsibleIntegration accordion with a custom logo,
 * status badge and content body that lets the salon claim chatity.io/<slug>.
 */
export default function ShortLinkBlock({ API }) {
  const { t } = useTranslation();
  const [currentSlug, setCurrentSlug] = useState(null);
  const [draft, setDraft] = useState("");
  const [check, setCheck] = useState({ status: "idle", reason: null, normalized: "" });
  const [saving, setSaving] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const qrCanvasRef = useRef(null);
  const checkAbortRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/slug/mine`);
      const slug = r.data?.slug || "";
      setCurrentSlug(slug);
      setDraft(slug || r.data?.suggested || "");
    } catch (e) { /* silent */ }
  };

  useMountEffect(load);

  // Debounced availability check
  useEffect(() => {
    if (!draft) {
      setCheck({ status: "idle", reason: null, normalized: "" });
      return;
    }
    if (draft === currentSlug) {
      setCheck({ status: "ok", reason: "current", normalized: draft });
      return;
    }
    const handle = setTimeout(async () => {
      try {
        if (checkAbortRef.current) checkAbortRef.current.abort();
        const controller = new AbortController();
        checkAbortRef.current = controller;
        setCheck({ status: "checking", reason: null, normalized: "" });
        const r = await axios.get(`${API}/slug/check`, {
          params: { slug: draft },
          signal: controller.signal,
        });
        if (r.data?.available) {
          setCheck({ status: "ok", reason: null, normalized: r.data.slug });
        } else {
          setCheck({ status: "taken", reason: r.data?.reason, normalized: r.data?.slug });
        }
      } catch (e) {
        if (e.name === "CanceledError" || e.name === "AbortError") return;
        setCheck({ status: "error", reason: e.message, normalized: "" });
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [draft, currentSlug, API]);

  const save = async () => {
    if (check.status !== "ok") return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/slug/mine`, { slug: draft });
      setCurrentSlug(r.data?.slug);
      setDraft(r.data?.slug);
      toast.success(t("shortLink.toast.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("shortLink.toast.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const fullUrl = currentSlug ? `https://chatity.io/${currentSlug}` : "";
  const copy = () => {
    if (!fullUrl) return;
    navigator.clipboard.writeText(fullUrl);
    toast.success(t("shortLink.toast.copied"));
  };

  // Download the rendered QR canvas as a PNG file named after the slug.
  // Uses the imperative canvas ref exposed by QRCodeCanvas to avoid any
  // tainted-canvas issues (logo is inlined SVG → same-origin pixel data).
  const downloadQr = () => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;
    try {
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `chatity-${currentSlug || "qr"}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(t("shortLink.toast.qrDownloaded"));
    } catch (e) {
      toast.error(t("shortLink.toast.qrDownloadFailed"));
    }
  };

  const friendlyReason = (reason) => {
    if (!reason) return t("shortLink.status.taken");
    if (reason.includes("зарезервирован")) return t("shortLink.reason.reserved");
    if (reason.includes("Уже занят")) return t("shortLink.reason.alreadyTaken");
    if (reason.includes("Минимум")) return t("shortLink.reason.tooShort");
    if (reason.includes("Максимум")) return t("shortLink.reason.tooLong");
    if (reason.includes("Только латиница")) return t("shortLink.reason.invalidChars");
    return reason;
  };

  // Matching logo style with VK/IDENT — square with brand colour + emoji/icon
  const logo = (
    <div className="w-full h-full rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-2xl">
      🔗
    </div>
  );

  return (
    <CollapsibleIntegration
      logo={logo}
      title={t("shortLink.title")}
      description={t("shortLink.description")}
      enabled={!!currentSlug}
      enabledLabel={t("shortLink.badge.active")}
      disabledLabel={t("shortLink.badge.notSet")}
      testId="short-link-block"
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-white border-2 border-black rounded-lg overflow-hidden flex-1 min-w-[260px]">
            <span className="px-3 py-2 text-gray-500 text-sm bg-gray-100 border-r-2 border-black select-none">
              chatity.io/
            </span>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value.toLowerCase().replace(/\s/g, '-'))}
              placeholder={t("shortLink.placeholder")}
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              maxLength={50}
              data-testid="short-link-input"
            />
          </div>

          <Button
            onClick={save}
            disabled={saving || check.status !== "ok" || draft === currentSlug}
            className="bg-black text-white hover:bg-gray-800"
            data-testid="short-link-save-btn"
          >
            {saving ? t("shortLink.saving") : t("shortLink.save")}
          </Button>
        </div>

        {/* Availability status */}
        <div className="text-sm min-h-[20px]" data-testid="short-link-status">
          {check.status === "checking" && (
            <span className="text-gray-500">{t("shortLink.status.checking")}</span>
          )}
          {check.status === "ok" && draft !== currentSlug && (
            <span className="text-green-700">
              ✓ {t("shortLink.status.available")}: <code className="bg-white px-1.5 py-0.5 border border-green-700 rounded">chatity.io/{check.normalized}</code>
            </span>
          )}
          {check.status === "ok" && draft === currentSlug && currentSlug && (
            <span className="text-blue-700">✓ {t("shortLink.status.currentLink")}</span>
          )}
          {check.status === "taken" && (
            <span className="text-red-700">✗ {friendlyReason(check.reason)}</span>
          )}
          {check.status === "error" && (
            <span className="text-red-700">{t("shortLink.status.error")}: {check.reason}</span>
          )}
        </div>

        {/* Active link with copy + open */}
        {currentSlug && (
          <div className="flex items-center gap-2 flex-wrap pt-3 border-t-2 border-gray-300">
            <code className="bg-white px-3 py-2 border-2 border-black rounded text-sm font-mono flex-1 min-w-[200px] truncate" data-testid="short-link-url">
              {fullUrl}
            </code>
            <Button
              onClick={copy}
              variant="outline"
              className="border-2 border-black text-black hover:bg-black hover:text-white"
              data-testid="short-link-copy-btn"
            >
              📋 {t("shortLink.copy")}
            </Button>
            <Button
              onClick={() => window.open(fullUrl, "_blank")}
              variant="outline"
              className="border-2 border-black text-black hover:bg-black hover:text-white"
              data-testid="short-link-open-btn"
            >
              🔗 {t("shortLink.open")}
            </Button>
            <Button
              onClick={() => setQrOpen(true)}
              variant="outline"
              className="border-2 border-black text-black hover:bg-black hover:text-white"
              data-testid="short-link-qr-btn"
            >
              📱 {t("shortLink.qr") || "QR-code"}
            </Button>
          </div>
        )}
      </div>

      {/* QR-code modal */}
      {qrOpen && currentSlug && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setQrOpen(false)}
          className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4 backdrop-blur-sm"
          data-testid="short-link-qr-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl border-2 border-black p-6 md:p-8 max-w-md w-full relative"
          >
            <button
              type="button"
              onClick={() => setQrOpen(false)}
              aria-label={t("shortLink.qr.close") || "Close"}
              className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
              data-testid="short-link-qr-close"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-xl md:text-2xl font-bold mb-2 pr-8">
              {t("shortLink.qr.title") || "Your QR-code"}
            </h3>
            <p className="text-sm text-gray-600 mb-5">
              {t("shortLink.qr.hint") || "Place it on print materials, business cards or shop window. Scanning opens your mini-site."}
            </p>

            <div className="flex justify-center mb-5">
              <div className="p-4 bg-white border-2 border-black rounded-xl">
                <QRCodeCanvas
                  ref={qrCanvasRef}
                  value={fullUrl}
                  size={256}
                  level="H"
                  marginSize={2}
                  fgColor="#000000"
                  bgColor="#ffffff"
                  data-testid="short-link-qr-canvas"
                />
              </div>
            </div>

            <code className="block bg-gray-50 text-center px-3 py-2 border-2 border-gray-200 rounded text-sm font-mono mb-5 truncate" data-testid="short-link-qr-url">
              {fullUrl}
            </code>

            <div className="flex gap-2">
              <Button
                onClick={downloadQr}
                className="flex-1 bg-black text-white hover:bg-gray-800 font-bold"
                data-testid="short-link-qr-download"
              >
                ⬇ {t("shortLink.qr.download") || "Download PNG"}
              </Button>
              <Button
                onClick={copy}
                variant="outline"
                className="flex-1 border-2 border-black text-black hover:bg-black hover:text-white"
                data-testid="short-link-qr-copy"
              >
                📋 {t("shortLink.copy")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </CollapsibleIntegration>
  );
}
